import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { GetProp, MenuProps } from "antd";
import { Divider, Dropdown, Menu, Space, Tooltip } from "antd";
import CommonStyles from "../components/CommonStyles";
import { baseRoutes } from "@base/routes/baseRoutes";
import { useTranslation } from "react-i18next";
import { menu } from "@base/constants";
import CommonImages from "@base/assets/images";
import { useSelector } from "react-redux";
import { RootState } from "@base/store/store";
import CommonIcons from "@base/assets/icons";
import React, { useEffect, useState } from "react";
import useWindowDimensions from "@base/hooks/useWindowDimensions";
import useToggle from "@base/hooks/useToggle";
import LanguageSwitcher from "./LanguageSwitcher";
import BrandLogo from "@components/BrandLogo";
import { SearchOutlined } from "@ant-design/icons";
import { TLinkTarget } from "@base/types";

type MenuItem = GetProp<MenuProps, "items">[number];

const HeaderLayout = () => {
  //! state
  const location = useLocation();
  const fullCurrentPath = location.pathname + (location.search ?? "");

  const navigate = useNavigate();
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.appCommonSlice.userInfo);
  const { width } = useWindowDimensions();
  const [closeMenu, setCloseMenu] = useState(false);
  const { open: openDrawer, shouldRender: shouldRenderDrawer, toggle: toggleDrawer } = useToggle();

  useEffect(() => {
    if (width >= 1024) {
      setCloseMenu(false);
    } else {
      setCloseMenu(true);
    }
  }, [width]);

  //! function
  const backToLogin = () => {
    navigate(baseRoutes.login);
  };

  const backToInfo = () => {
    navigate(baseRoutes.user);
  };

  const navigateSearch = () => {
    navigate(baseRoutes.search);
  };

  const existActiveChildRoute = (
    children?: {
      href: string;
      target?: TLinkTarget;
      label: string;
    }[],
  ) => {
    if (!children?.length) {
      return false;
    }

    let exist = children.find((child) => {
      return fullCurrentPath.includes(child.href);
    });

    return !!exist;
  };

  //! render
  const menuItems = (): MenuItem[] => {
    return menu(baseRoutes).map((route, index) => {
      if (route.children) {
        return {
          key: index,
          label: (
            <CommonStyles.NavLinkTextUI
              isActive={(!!route.href && fullCurrentPath.includes(route.href)) || existActiveChildRoute(route.children)}
              text={route.label}
              key={index}
              target={route.target}
            />
          ),
          children: route.children?.map((child, idx) => {
            return {
              key: `${index}-${idx}`,
              label: (
                <CommonStyles.NavLinkTextUI
                  isActive={fullCurrentPath.includes(child.href)}
                  text={child.label}
                  linkTo={child.href}
                  key={`${index}-${idx}`}
                  target={route.target}
                />
              ),
            };
          }),
        };
      } else {
        return {
          key: index,
          label: (
            <CommonStyles.NavLinkTextUI
              isActive={!!route.href && fullCurrentPath.includes(route.href)}
              text={route.label}
              linkTo={route.href}
              key={index}
              target={route.target}
            />
          ),
        };
      }
    });
  };

  const renderMenu = () => {
    return menu(baseRoutes).map((route, index) => {
      if (route.children) {
        const itemChildren = route.children.map((child, idx) => {
          return {
            key: idx,
            label: (
              <CommonStyles.NavLinkTextUI
                className="text-base"
                isActive={fullCurrentPath.includes(child.href)}
                text={child.label}
                linkTo={child.href}
                target={child.target}
              />
            ),
          };
        });

        return (
          <Dropdown
            menu={{ items: itemChildren }}
            dropdownRender={(menu) => (
              <div>
                {React.cloneElement(menu as React.ReactElement)}
                <Divider style={{ margin: 0 }} />
              </div>
            )}
            key={index}
          >
            <Space className="cursor-pointer">
              <CommonStyles.NavLinkTextUI
                linkTo={route.href}
                className="text-base font-medium"
                isActive={(!!route.href && fullCurrentPath.includes(route.href)) || existActiveChildRoute(route.children)}
                text={t(route.label)}
                target={route.target}
              />
              <CommonIcons.IconDown />
            </Space>
          </Dropdown>
        );
      } else {
        return (
          <NavLink key={index} to={String(route.href)} target={route.target}>
            <CommonStyles.NavLinkTextUI
              className="text-base font-medium"
              isActive={!!route.href && fullCurrentPath.includes(route.href)}
              text={t(route.label)}
              target={route.target}
            />
          </NavLink>
        );
      }
    });
  };

  return (
    <div className="bg-common-white py-4 flex items-center justify-between gap-5">
      <div className="flex justify-between w-full lg:hidden xl:block xl:w-fit gap-5">
        <div
          className="flex-none w-fit cursor-pointer flex items-center"
          onClick={() => {
            navigate(baseRoutes.home);
          }}
        >
          <BrandLogo className="text-[44px] max-sm:text-[38px]" />
        </div>

        {closeMenu && (
          <div className="flex items-center gap-3">
            <Tooltip title="Tra cứu nhanh">
              <CommonStyles.ButtonUI onClick={navigateSearch} type="default" shape="circle" icon={<SearchOutlined />} />
            </Tooltip>

            <div className="xl:hidden cursor-pointer" onClick={toggleDrawer}>
              {<CommonIcons.IconMenu />}
            </div>
          </div>
        )}
      </div>

      <div className="grow nav-bar lg:flex xl:justify-center items-center hidden">
        <nav className="flex flex-row gap-5 flex-nowrap whitespace-nowrap">{renderMenu()}</nav>
      </div>

      <div className="flex-none w-fit actions flex items-center gap-4">
        {!closeMenu ? (
          <Tooltip title="Tra cứu nhanh">
            <CommonStyles.ButtonUI onClick={navigateSearch} type="default" shape="circle" icon={<SearchOutlined />} />
          </Tooltip>
        ) : null}

        {!closeMenu && <LanguageSwitcher />}

        {!closeMenu && (
          <>
            {userInfo?.user_name ? (
              <div className="w-fit h-fit p-[2px] rounded-[50%] border border-common-primary cursor-pointer" onClick={backToInfo}>
                <img
                  className="rounded-[50%]"
                  width={30}
                  height={30}
                  src={
                    userInfo?.user_profiles?.avatar && userInfo?.user_profiles?.avatar.length > 0
                      ? userInfo?.user_profiles?.avatar[0].link
                      : CommonImages.DefaultAvatar
                  }
                  alt=""
                />
              </div>
            ) : (
              <CommonStyles.ButtonUI className="rounded-[30px]" type="primary" onClick={backToLogin}>
                <span className="text-base font-medium font-['Inter']">{t("Register/Login")}</span>
              </CommonStyles.ButtonUI>
            )}
          </>
        )}
      </div>

      {shouldRenderDrawer && (
        <CommonStyles.DrawerUI
          title={
            userInfo?.user_name ? (
              <CommonStyles.TypographyUI
                className="cursor-pointer text-common-primary font-medium"
                onClick={() => {
                  backToInfo();
                  toggleDrawer();
                }}
              >
                {userInfo?.user_profiles?.display_name ?? userInfo.user_name}
              </CommonStyles.TypographyUI>
            ) : (
              <span
                onClick={() => {
                  backToLogin();
                  toggleDrawer();
                }}
                className="text-base font-medium font-['Inter'] cursor-pointer text-common-primary"
              >
                {t("Register/Login")}
              </span>
            )
          }
          width={300}
          open={openDrawer}
          onClose={toggleDrawer}
        >
          <Menu className="border-none" mode="inline" items={menuItems()} onSelect={toggleDrawer} />
          <Divider className="my-2" />
          <LanguageSwitcher variant="select" onChanged={toggleDrawer} />
        </CommonStyles.DrawerUI>
      )}
    </div>
  );
};

export default HeaderLayout;
